<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import TabBar from '@/components/TabBar.vue'
import { useAuth } from '@/composables/useAuth'
import { useCompetition } from '@/composables/useCompetition'
import { provideScrollContainer } from '@/composables/useScrollContainer'

const route = useRoute()
const router = useRouter()

// le catalogue vient de l'API Olympe : on le charge au démarrage, et c'est lui
// qui fixe ensuite la couleur d'accent de la compétition sélectionnée
const { loadCompetitions } = useCompetition()

// la session OIDC : écoute des liens de retour (natif), traitement d'un code déjà
// présent dans l'URL (web) ou restauration de la session persistée
const { initAuth } = useAuth()

/** conteneur scrollable : c'est lui qui scrolle, pas le document */
const mainEl = ref<HTMLElement | null>(null)
// le rend disponible aux composants qui réagissent au défilement (parallaxe…)
provideScrollContainer(mainEl)

/** le shell : c'est lui qui porte `--scrim` (et le scrim lui-même) */
const shellEl = ref<HTMLElement | null>(null)

// Scrim de barre de statut : le contenu passe sous l'encoche (padding, pas de
// marge), donc dès qu'il défile il vient concurrencer l'heure et la batterie.
// On révèle alors un dégradé du fond vers le transparent, proportionnel aux 24
// premiers pixels de défilement — invisible à la butée haute, où le haut de
// page est déjà le fond de page. Piloté en custom property plutôt qu'en state
// réactif : pas de re-render de l'app à chaque `scroll`.
const SCRIM_DISTANCE = 24
let lastScrim = -1

function setScrim(value: number): void {
  const element = shellEl.value
  if (!element) return
  // arrondi à 2 décimales : on n'écrit dans le style que sur un vrai changement
  const scrim = Math.round(value * 100) / 100
  if (scrim === lastScrim) return
  lastScrim = scrim
  element.style.setProperty('--scrim', `${scrim}`)
}

function onMainScroll(): void {
  // `scrollTop` passe sous 0 au rebond iOS : on le borne, sinon le scrim
  // « remonterait » dans les négatifs
  const scrollTop = Math.max(mainEl.value?.scrollTop ?? 0, 0)
  setScrim(Math.min(scrollTop / SCRIM_DISTANCE, 1))
}

/* --- Position de défilement, par écran -------------------------------------
   HIG `tab-bars` : « quickly switch between sections while preserving the
   current navigation state », et `launching` : « restore … scroll view to most
   recent position ». L'app remettait le scroll à zéro à CHAQUE changement de
   route : revenir d'une fiche d'équipe, ou d'un onglet à l'autre, reperdait le
   lecteur en haut de la liste qu'il venait de parcourir.

   On mémorise donc la position de chaque `fullPath` quitté, et on la rend à
   l'arrivée. Un écran jamais vu (une autre fiche de match, une autre équipe)
   n'a pas d'entrée : il s'ouvre en haut, comme avant.

   La carte n'est pas un `ref` : rien ne la rend à l'écran, et elle ne vit que
   le temps de la session (aucun stockage web, cf. l'invariant de CLAUDE.md).
   Elle est bornée par le nombre d'écrans réellement visités.

   LIMITE, à vérifier à la passe [device] : la restitution a lieu à l'insertion
   de la nouvelle vue (`@enter` de la transition), donc avec le contenu déjà
   rendu **de façon synchrone** — le cache d'une liste déjà chargée, par
   exemple. Une vue dont le contenu n'arrive qu'après une requête réseau n'a
   pas encore la hauteur nécessaire : le navigateur borne alors le `scrollTop`
   et l'écran s'ouvre plus haut que prévu. */
const scrollPositions = new Map<string, number>()

function rememberScroll(fullPath: string): void {
  scrollPositions.set(fullPath, mainEl.value?.scrollTop ?? 0)
}

/** Appelée à l'insertion de la vue entrante, cf. `@enter` sur la transition. */
function restoreScroll(): void {
  const top = scrollPositions.get(route.fullPath) ?? 0
  mainEl.value?.scrollTo({ top })
  // le `scrollTo` n'émet pas toujours de `scroll` : on remet le scrim d'accord
  // avec la position qu'on vient d'imposer
  setScrim(Math.min(Math.max(top, 0) / SCRIM_DISTANCE, 1))
}

/** relevé AVANT que la vue quittée ne soit démontée */
const stopRecording = router.beforeEach((_to, from) => {
  if (from.fullPath) rememberScroll(from.fullPath)
})

onMounted(() => {
  void loadCompetitions()
  void initAuth()
  mainEl.value?.addEventListener('scroll', onMainScroll, { passive: true })
})

onBeforeUnmount(() => {
  mainEl.value?.removeEventListener('scroll', onMainScroll)
  stopRecording()
})
</script>

<template>
  <!--
    `app-shell--home` : l'accueil (catalogue + module « À l'affiche ») est le
    seul écran au fond NEUTRE — il suit le thème système (clair / sombre) comme
    le reste de l'app, mais sans la teinte de la compétition, qui entrerait en
    conflit avec les couleurs de toutes les jaquettes. Le bloc de tokens
    correspondant vit dans `variables.css` (global), pas ici : ces styles sont
    `scoped`, et de toute façon les custom properties héritent, donc la classe
    sur `.app-shell` suffit à reteindre le contenu ET la tab bar.
  -->
  <div ref="shellEl" class="app-shell" :class="{ 'app-shell--home': route.name === 'home' }">
    <main ref="mainEl" class="app-main">
      <!-- `@enter` (et non `@after-enter`) : la position est rendue dès que la
           vue entrante est insérée, avant l'animation — sinon le contenu
           s'afficherait 180 ms en haut de page puis sauterait -->
      <RouterView v-slot="{ Component }">
        <Transition name="page" mode="out-in" @enter="restoreScroll">
          <component :is="Component" />
        </Transition>
      </RouterView>
    </main>

    <!-- voile de la barre de statut : purement décoratif, et il ne doit pas
         intercepter le glissement du contenu qui passe dessous -->
    <div class="app-shell__scrim" aria-hidden="true" />

    <TabBar />
  </div>
</template>

<style scoped>
.app-shell {
  /* dvh : suit la barre d'URL mobile ; fallback vh pour les vieilles webviews */
  height: 100vh;
  height: 100dvh;
  overflow: hidden;
  background-color: var(--color-bg);
  /* accueil ↔ autres onglets : le fond passe du neutre (thème système) au fond
     teinté par la compétition — sans transition, le changement claquerait */
  transition: background-color 0.25s var(--ease-ios);
  /* repère de la tab bar, qui passe en `position: absolute` (cf. TabBar.vue) */
  position: relative;
}

/* PWA installée (iOS 26, bug WebKit 301108) : cf. commentaire dans
   `global.css`. Même correctif, appliqué ici à `.app-shell` puisque c'est
   elle, pas `html`/`body`, qui donne sa taille à la tab bar absolue. */
@media (display-mode: standalone), (display-mode: fullscreen) {
  .app-shell {
    height: 100lvh;
  }
}

.app-main {
  height: 100%;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  /* le scroll ne « fuit » pas vers le document parent */
  overscroll-behavior-y: contain;
  /* pas de barre de défilement visible : c'est une app, pas une page web */
  scrollbar-width: none;

  /* plus de header : on ne réserve que l'encoche (barre de statut)... */
  padding-top: var(--safe-top);
  /* ...et on réserve la place de la barre flottante + 8px de respiration */
  padding-bottom: calc(var(--tabbar-reserved) + 8px);
}

.app-main::-webkit-scrollbar {
  display: none;
}

/* Scrim de barre de statut, façon Netflix : un dégradé du fond de page vers le
   transparent, qui rend l'heure et la batterie lisibles quand du contenu passe
   sous l'encoche. `absolute` dans `.app-shell` et pas `fixed`, pour la même
   raison que la tab bar (bug WebKit 301108 en PWA installée : un élément
   `fixed` s'arrête ~47px au-dessus du bas de l'écran). Pas de couleur en dur :
   `--color-bg` suit le thème, l'accent de la compétition et le fond neutre de
   l'accueil (`app-shell--home`). L'opacité vient de `--scrim`, posée sur
   `.app-shell` par le script au fil du défilement : 0 à la butée haute. Un
   aplat en dégradé linéaire à pleine opacité faisait une bande trop visible
   (repérée façon Netflix mobile, où le voile ne se devine qu'à peine) : le
   fond est donc un aplat opaque, et c'est un masque qui dessine le fondu — il
   ne travaille que l'alpha (`#000`/`rgba(0,0,0,x)` ici n'est pas une couleur de
   thème, juste de la transparence), avec une courbe qui s'éteint en douceur
   plutôt qu'en ligne droite ; `--scrim-max` plafonne en plus l'opacité pour
   qu'il reste discret même à pleine valeur de `--scrim`. */
.app-shell__scrim {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: calc(var(--safe-top) + 32px);
  /* sous la tab bar (z-index 100), mais au-dessus du contenu qui défile */
  z-index: 90;
  pointer-events: none;
  background: var(--color-bg);
  -webkit-mask-image: linear-gradient(to bottom, #000 0%, rgba(0, 0, 0, 0.6) 35%, rgba(0, 0, 0, 0.25) 65%, rgba(0, 0, 0, 0.06) 88%, transparent 100%);
  mask-image: linear-gradient(to bottom, #000 0%, rgba(0, 0, 0, 0.6) 35%, rgba(0, 0, 0, 0.25) 65%, rgba(0, 0, 0, 0.06) 88%, transparent 100%);
  --scrim-max: 0.55;
  opacity: calc(var(--scrim, 0) * var(--scrim-max));
  transition: opacity 180ms ease-out;
}

/* le voile reste (c'est de la lisibilité, pas de la décoration) : seul le
   fondu saute */
@media (prefers-reduced-motion: reduce) {
  .app-shell__scrim {
    transition: none;
  }
}

/* Transition de page discrète, façon iOS */
.page-enter-active,
.page-leave-active {
  transition:
    opacity 0.18s var(--ease-ios),
    transform 0.18s var(--ease-ios);
}

.page-enter-from {
  opacity: 0;
  transform: translateY(6px);
}

.page-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}
</style>
